/**
 * Design tokens. Two themes share one set of token names so components never branch on theme.
 *
 *  - clarity (default): high contrast (AAA body text), hyperlegible type, no texture, minimal motion.
 *  - midnight: deeper ink, gold-leaf hairlines, a faint star field and almanac-press headings.
 *    Body text still meets WCAG AA; the extra mood lives in backgrounds and ornament, never in text.
 */

export type ThemeName = "clarity" | "midnight";

export interface ThemeTokens {
  bg: string;
  surface: string;
  surfaceRaised: string;
  border: string;
  text: string;
  textMuted: string;
  accent: string;
  accentText: string;
  highlight: string;
  danger: string;
  warning: string;
  ok: string;
  focus: string;
  fontBody: string;
  fontDisplay: string;
}

const body = `"Atkinson Hyperlegible Next", "Atkinson Hyperlegible", system-ui, sans-serif`;

export const THEMES: Record<ThemeName, ThemeTokens> = {
  clarity: {
    bg: "#17131f", // inkwell aubergine
    surface: "#221c2d", // night vellum
    surfaceRaised: "#2d2539",
    border: "#6f6380",
    text: "#f1ebdf", // parchment
    textMuted: "#cfc6d9",
    accent: "#d6a2e8", // foxglove
    accentText: "#17131f",
    highlight: "#e8c27a", // beeswax
    danger: "#ff9e9e",
    warning: "#f2c66d",
    ok: "#a6dcaa",
    focus: "#ffe08a",
    fontBody: body,
    fontDisplay: body,
  },
  midnight: {
    bg: "#0f0b16",
    surface: "#18121f",
    surfaceRaised: "#211a2b",
    border: "#5e4a2c", // gold leaf, aged
    text: "#e7dfd0",
    textMuted: "#b4a9c2",
    accent: "#c9a0dc",
    accentText: "#0f0b16",
    highlight: "#c9a35a",
    danger: "#f08f8f",
    warning: "#e2b65e",
    ok: "#94c99a",
    focus: "#f5d78a",
    fontBody: body,
    fontDisplay: `"IM Fell English", Georgia, serif`,
  },
};

const kebab = (s: string) => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

/** CSS custom properties for every theme, selected by `data-theme` on <html>. */
export function themeCss(themes: Record<ThemeName, ThemeTokens> = THEMES): string {
  return Object.entries(themes)
    .map(([name, tokens]) => {
      const vars = Object.entries(tokens)
        .map(([k, v]) => `  --hx-${kebab(k)}: ${v};`)
        .join("\n");
      const selector =
        name === "clarity" ? `:root, [data-theme="clarity"]` : `[data-theme="${name}"]`;
      return `${selector} {\n${vars}\n}`;
    })
    .join("\n");
}

// ---- WCAG contrast (https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio)

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) throw new Error(`Expected #rrggbb, got ${hex}`);
  const n = parseInt(m[1]!, 16);
  return (
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
  );
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
